import React, { useState, useEffect, useRef } from 'react';
import { 
  UserCircleIcon, 
  BuildingStorefrontIcon, 
  ShieldCheckIcon,
  BellAlertIcon,
  LanguageIcon,
  ArrowRightOnRectangleIcon,
  CheckBadgeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { updateProfileApi } from '../../api/authApi';
import { businessApi } from '../../api/businessApi';
import api from '../../api/axiosConfig';
import SmtpSettings from '../../components/settings/SmtpSettings';

const Settings = () => {
  const { logout, user, setUser, activeBusiness, setBusinesses } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Local state to hold form data so it updates when 'user' is loaded
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    businessName: '',
    gstin: '',
    profilePicture: '',
    allowedPaidLeavesPerMonth: 2,
    monthlySalesTarget: 500000,
    gstRate: 18,
    upiId: '',
    signatureUrl: ''
  });

  useEffect(() => {
    if (user || activeBusiness) {
      setFormData({
        name: user?.name || '',
        phone: user?.phone || '',
        businessName: activeBusiness?.name || '',
        gstin: activeBusiness?.gstNumber || '',
        profilePicture: user?.profilePicture || '',
        allowedPaidLeavesPerMonth: activeBusiness?.settings?.allowedPaidLeavesPerMonth ?? 2,
        monthlySalesTarget: activeBusiness?.settings?.monthlySalesTarget ?? 500000,
        gstRate: activeBusiness?.settings?.gstRate ?? 18,
        upiId: activeBusiness?.payment?.upiId || '',
        signatureUrl: activeBusiness?.assets?.signatureUrl || ''
      });
    }
  }, [user, activeBusiness]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      // 1. Get ImageKit Auth Params
      const authRes = await api.get('/v1/imagekit');
      const { token, expire, signature, publicKey } = authRes.data;

      // 2. Upload to ImageKit
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('fileName', file.name);
      uploadData.append('publicKey', publicKey);
      uploadData.append('signature', signature);
      uploadData.append('expire', expire);
      uploadData.append('token', token);

      const uploadRes = await fetch('https://upload.imagekit.io/api/v1/files/upload', {
        method: 'POST',
        body: uploadData
      });
      const uploadJson = await uploadRes.json();

      if (uploadJson.url) {
        setFormData((prev) => ({ ...prev, profilePicture: uploadJson.url }));
        toast.success('Profile picture uploaded! Click "Save Changes" to confirm.');
      } else {
        toast.error('Upload failed: ' + (uploadJson.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('ImageKit upload error:', error);
      toast.error('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      
      // 1. Update Personal Profile
      const res = await updateProfileApi(formData);
      if (res.data && res.data.data && res.data.data.user) {
        setUser(res.data.data.user);
      }

      // 2. Update Business Profile (If active business exists)
      if (activeBusiness) {
        const businessUpdateData = {
          name: formData.businessName,
          gstNumber: formData.gstin,
          allowedPaidLeavesPerMonth: Number(formData.allowedPaidLeavesPerMonth),
          monthlySalesTarget: Number(formData.monthlySalesTarget),
          gstRate: Number(formData.gstRate),
          upiId: formData.upiId,
          signatureUrl: formData.signatureUrl
        };
        const bizRes = await businessApi.updateBusiness(activeBusiness._id, businessUpdateData);
        // Updating context slightly (ideally refetch, but this is okay for immediate UI update)
        if (bizRes.data?.data) {
          activeBusiness.name = bizRes.data.data.name;
          activeBusiness.gstNumber = bizRes.data.data.gstNumber;
          if (!activeBusiness.settings) activeBusiness.settings = {};
          activeBusiness.settings.allowedPaidLeavesPerMonth = bizRes.data.data.settings?.allowedPaidLeavesPerMonth;
          activeBusiness.settings.monthlySalesTarget = bizRes.data.data.settings?.monthlySalesTarget;
          activeBusiness.settings.gstRate = bizRes.data.data.settings?.gstRate;
          if (!activeBusiness.payment) activeBusiness.payment = {};
          activeBusiness.payment.upiId = bizRes.data.data.payment?.upiId;
          if (!activeBusiness.assets) activeBusiness.assets = {};
          activeBusiness.assets.signatureUrl = bizRes.data.data.assets?.signatureUrl;
        }
      }

      toast.success('Settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full pb-10 max-w-full mx-auto space-y-6">
      
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Settings</h1>
        <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-1">Manage your account preferences and business profile.</p>
      </div>

      {/* Profile Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-colors">
        <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
          <div className="p-2 bg-red-50 dark:bg-red-900/20 text-[#dc343b] dark:text-red-400 rounded-lg">
            <UserCircleIcon className="w-5 h-5" />
          </div>
          <h2 className="text-[16px] font-bold text-gray-900 dark:text-white">Personal Information</h2>
        </div>
        
        <div className="p-6">
          <div className="flex items-center gap-5 mb-6">
            <div className="h-16 w-16 bg-gradient-to-br from-red-100 to-red-50 rounded-full flex items-center justify-center text-[#dc343b] font-bold text-xl border-2 border-white shadow-sm overflow-hidden relative">
              {formData.profilePicture ? (
                <img src={formData.profilePicture} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                formData.name ? formData.name.charAt(0).toUpperCase() : 'U'
              )}
              {uploading && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                  <ArrowPathIcon className="w-5 h-5 animate-spin text-[#dc343b]" />
                </div>
              )}
            </div>
            <div>
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*"
                onChange={handleImageUpload}
              />
              <button 
                onClick={() => fileInputRef.current.click()}
                disabled={uploading}
                className="text-[13px] font-bold text-[#dc343b] hover:text-red-700 transition-colors disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Change Photo'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Full Name</label>
              <input 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#dc343b]/20 focus:border-[#dc343b] transition-all outline-none text-[14px] font-medium text-gray-900 dark:text-white" 
              />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Phone Number</label>
              <div className="relative">
                <input 
                  type="text" 
                  name="phone"
                  value={formData.phone}
                  readOnly 
                  className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl outline-none text-[14px] font-medium text-gray-500 dark:text-gray-400 cursor-not-allowed" 
                />
                <CheckBadgeIcon className="w-5 h-5 text-green-500 absolute right-3 top-3" />
              </div>
            </div>
          </div>
          
          <div className="mt-5 flex justify-end">
            <button 
              onClick={handleSaveProfile}
              disabled={saving}
              className="bg-gray-900 text-white px-5 py-2 rounded-lg font-bold text-[13px] hover:bg-gray-800 transition-colors disabled:opacity-70 flex items-center gap-2"
            >
              {saving ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : null}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Business Details Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-colors">
        <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">
            <BuildingStorefrontIcon className="w-5 h-5" />
          </div>
          <h2 className="text-[16px] font-bold text-gray-900 dark:text-white">Business Details</h2>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Business Name</label>
              <input 
                type="text" 
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                placeholder="Enter business name" 
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#dc343b]/20 focus:border-[#dc343b] transition-all outline-none text-[14px] font-medium text-gray-900 dark:text-white" 
              />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">GSTIN (Optional)</label>
              <input 
                type="text" 
                name="gstin"
                value={formData.gstin}
                onChange={handleChange}
                placeholder="Enter GST number" 
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#dc343b]/20 focus:border-[#dc343b] transition-all outline-none text-[14px] font-medium text-gray-900 dark:text-white uppercase" 
              />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Allowed Paid Leaves (Per Month)</label>
              <input 
                type="number" 
                name="allowedPaidLeavesPerMonth"
                value={formData.allowedPaidLeavesPerMonth}
                onChange={handleChange}
                placeholder="E.g., 2" 
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#dc343b]/20 focus:border-[#dc343b] transition-all outline-none text-[14px] font-medium text-gray-900 dark:text-white" 
              />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Monthly Sales Target (₹)</label>
              <input 
                type="number" 
                name="monthlySalesTarget"
                value={formData.monthlySalesTarget}
                onChange={handleChange}
                placeholder="E.g., 500000" 
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#dc343b]/20 focus:border-[#dc343b] transition-all outline-none text-[14px] font-medium text-gray-900 dark:text-white" 
              />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Default GST Rate (%)</label>
              <input 
                type="number" 
                name="gstRate"
                value={formData.gstRate}
                onChange={handleChange}
                placeholder="E.g., 18" 
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#dc343b]/20 focus:border-[#dc343b] transition-all outline-none text-[14px] font-medium text-gray-900 dark:text-white" 
              />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">UPI ID (For Payments)</label>
              <input 
                type="text" 
                name="upiId"
                value={formData.upiId}
                onChange={handleChange}
                placeholder="e.g. name@upi" 
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#dc343b]/20 focus:border-[#dc343b] transition-all outline-none text-[14px] font-medium text-gray-900 dark:text-white" 
              />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Digital Signature Image URL (Optional)</label>
              <input 
                type="text" 
                name="signatureUrl"
                value={formData.signatureUrl}
                onChange={handleChange}
                placeholder="https://example.com/signature.png" 
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#dc343b]/20 focus:border-[#dc343b] transition-all outline-none text-[14px] font-medium text-gray-900 dark:text-white" 
              />
            </div>
          </div>
          
          <div className="mt-5 flex justify-end">
            <button 
              onClick={handleSaveProfile}
              disabled={saving}
              className="bg-gray-900 text-white px-5 py-2 rounded-lg font-bold text-[13px] hover:bg-gray-800 transition-colors disabled:opacity-70 flex items-center gap-2"
            >
              {saving ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : null}
              {saving ? 'Updating...' : 'Update Business'}
            </button>
          </div>
        </div>
      </div>

      {/* App Preferences */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-colors">
        <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
          <div className="p-2 bg-orange-50 dark:bg-orange-900/20 text-orange-500 dark:text-orange-400 rounded-lg">
            <BellAlertIcon className="w-5 h-5" />
          </div>
          <h2 className="text-[16px] font-bold text-gray-900 dark:text-white">App Preferences</h2>
        </div>
        
        <div className="divide-y divide-gray-50 dark:divide-gray-700">
          <div className="p-5 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors">
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white text-[14px]">SMS Alerts</h3>
              <p className="text-gray-500 dark:text-gray-400 text-[12px] mt-0.5">Send automatic SMS to customers for new entries.</p>
            </div>
            <div className="w-11 h-6 bg-[#dc343b] rounded-full relative cursor-pointer shadow-inner">
              <div className="w-4 h-4 bg-white rounded-full absolute right-1 top-1 shadow-sm transition-all"></div>
            </div>
          </div>

          <div className="p-5 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors">
            <div className="flex items-center gap-3">
              <LanguageIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white text-[14px]">Language</h3>
                <p className="text-gray-500 dark:text-gray-400 text-[12px] mt-0.5">Choose your preferred app language.</p>
              </div>
            </div>
            <select className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 font-bold text-[12px] text-gray-700 dark:text-gray-300 outline-none cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800">
              <option>English</option>
              <option>Hindi</option>
            </select>
          </div>
        </div>
      </div>

      {/* SMTP Settings */}
      <SmtpSettings />

      {/* Security & Danger Zone */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-colors">
        <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
          <div className="p-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg">
            <ShieldCheckIcon className="w-5 h-5" />
          </div>
          <h2 className="text-[16px] font-bold text-gray-900 dark:text-white">Security</h2>
        </div>
        
        <div className="p-5 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white text-[14px]">Account Access</h3>
            <p className="text-gray-500 dark:text-gray-400 text-[12px] mt-0.5">Secure your account or sign out from this device.</p>
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <button className="flex-1 sm:flex-none px-4 py-2 text-[13px] font-bold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
              Change Password
            </button>
            <button 
              onClick={logout} 
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-[13px] font-bold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Settings;
